import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../../components/Navbar";

import { createArtwork, uploadMyArtworkImage } from "../../services/arthouseApi";

// extracted merch editor
import MerchLinksEditor, {
  merchBuild,
  DEFAULT_LINK,
} from "../../components/ArtHouse/MerchLinksEditor";

/**
 * NewArtworkPage (matches EditArtworkPage features + style)
 * - Upload image to R2 after create
 * - Title / Caption / Tags
 * - Merch links editor (same component as Edit)
 *
 * FIX:
 * MerchLinksEditor may store extra fields (label/colors/etc) using slightly different key names.
 * This page normalizes merch links into the backend/DB schema before sending.
 */

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function splitCsvToArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String).map((x) => x.trim()).filter(Boolean);
  return String(v)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Normalize any merch object shape into the DB schema:
 * product_type, purchase_url, label, platform, category, type,
 * price, currency, sizes (text[]), colors (text[]), note, is_primary
 */
function normalizeMerchLinksForApi(list) {
  if (!Array.isArray(list)) return [];

  return list
    .map((m) => {
      const product_type = String(
        m?.product_type ?? m?.productType ?? m?.type_name ?? ""
      )
        .trim();

      const purchase_url = String(
        m?.purchase_url ?? m?.purchaseUrl ?? m?.url ?? ""
      )
        .trim();

      const label = String(
        m?.label ?? m?.button_name ?? m?.buttonName ?? ""
      ).trim();

      const platform = String(m?.platform ?? "").trim();
      const category = String(m?.category ?? "").trim();

      // "type" is already a column in your table; keep it if you use it.
      const type = String(m?.type ?? m?.item_type ?? "").trim();

      const currency = String(m?.currency ?? "").trim();

      const priceRaw = m?.price;
      const price =
        priceRaw === "" || priceRaw === null || priceRaw === undefined
          ? null
          : Number(priceRaw);

      const sizesArr = splitCsvToArray(m?.sizes ?? m?.size_options);
      const colorsArr = splitCsvToArray(m?.colors ?? m?.color_options);

      const note = String(m?.note ?? m?.notes ?? "").trim();

      const is_primary = !!(m?.is_primary ?? m?.isPrimary);

      return {
        product_type,
        purchase_url,
        label: label || null,
        platform: platform || null,
        category: category || null,
        type: type || null,
        price: Number.isFinite(price) ? price : null,
        currency: currency || null,
        sizes: sizesArr.length ? sizesArr : null,   // keep as array for Postgres text[]
        colors: colorsArr.length ? colorsArr : null, // keep as array for Postgres text[]
        note: note || null,
        is_primary,
      };
    })
    .filter((m) => m.product_type && m.purchase_url);
}

export default function NewArtworkPage({
  embedded = false,
  onCancel,
  onPublished,
}) {
  const nav = useNavigate();

  const [saving, setSaving] = useState(false);

  // image upload state (R2)
  const [selectedFile, setSelectedFile] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const [flash, setFlash] = useState("");

  // IG-style draft fields (same as Edit)
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [tagsText, setTagsText] = useState("");

  // merch editor state
  const [links, setLinks] = useState([DEFAULT_LINK()]);

  const tagsArr = useMemo(() => parseTags(tagsText), [tagsText]);

  // build payload + validation (same as Edit)
  const { merchLinks, merchErrors } = useMemo(() => merchBuild(links), [links]);

  // normalize into backend schema BEFORE sending
  const normalizedMerchLinks = useMemo(
    () => normalizeMerchLinksForApi(merchLinks),
    [merchLinks]
  );

  const canPublish = useMemo(() => {
    if (saving) return false;
    if (!title.trim()) return false;
    if (!caption.trim()) return false;
    if (!selectedFile) return false; // require image on create
    if (merchErrors.length) return false;
    return true;
  }, [saving, title, caption, selectedFile, merchErrors.length]);

  const handleCancel = () => {
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    nav("/dashboard/art");
  };

  async function onPublish() {
    if (!canPublish) return;

    setSaving(true);
    setErrorMsg("");
    setFlash("");

    try {
      // Step 1: create artwork (include normalized merch links)
      const created = await createArtwork({
        title: title.trim(),
        description: caption.trim(),
        tags: tagsArr,
        tags_text: tagsArr.join(", "),

        // send the normalized schema in every alias your backend accepts
        merchLinks: normalizedMerchLinks,
        merch_links: normalizedMerchLinks,
        merch_links_array: normalizedMerchLinks,
        merch: normalizedMerchLinks,
      });

      // Resolve artwork id (support multiple backend shapes)
      const createdId =
        created?.artwork?.id ??
        created?.artwork?.artwork_id ??
        created?.artwork_id ??
        created?.id ??
        created?.data?.artwork?.id ??
        created?.data?.artwork_id ??
        null;

      // Step 2: upload image (required)
      if (selectedFile && createdId) {
        await uploadMyArtworkImage(createdId, selectedFile);
      }

      setFlash("Published ");
      setTimeout(() => setFlash(""), 900);

      if (typeof onPublished === "function") {
        onPublished({ artworkId: createdId, artwork: created?.artwork ?? null });
        return;
      }

      nav("/dashboard/art", { replace: true });
    } catch (e) {
      const backendMsg =
        e?.data?.error ||
        e?.data?.message ||
        e?.message ||
        "Failed to publish artwork.";
      setErrorMsg(backendMsg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={
        embedded
          ? "h-full bg-transparent text-white"
          : "min-h-screen bg-[#070B16] text-white"
      }
    >
      {!embedded && <Navbar />}

      {/* Premium ambience: glows + radial + subtle grid */}
      {!embedded ? (
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-44 left-16 h-[560px] w-[560px] rounded-full bg-cyan-400/10 blur-[150px]" />
          <div className="absolute top-28 -right-52 h-[720px] w-[720px] rounded-full bg-sky-400/10 blur-[170px]" />
          <div className="absolute -bottom-60 left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-indigo-400/5 blur-[170px]" />

          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.09),transparent_45%),radial-gradient(circle_at_80%_18%,rgba(125,211,252,0.07),transparent_50%),radial-gradient(circle_at_55%_80%,rgba(99,102,241,0.06),transparent_55%)]" />

          <div className="absolute inset-0 opacity-[0.16] [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:44px_44px]" />
        </div>
      ) : null}

      <div className="relative mx-auto w-full max-w-[1200px] px-4 pt-8 pb-16">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {embedded ? (
              <button
                type="button"
                onClick={handleCancel}
                className="
                  inline-flex items-center gap-2
                  rounded-full border border-white/10
                  bg-white/[0.05] hover:bg-white/[0.08]
                  px-4 py-2
                  text-sm font-semibold text-white/80
                  shadow-[0_18px_70px_rgba(0,0,0,0.25)]
                  transition
                "
              >
                Close
              </button>
            ) : (
              <Link
                to="/dashboard/art"
                className="
                  inline-flex items-center gap-2
                  rounded-full border border-white/10
                  bg-white/[0.05] hover:bg-white/[0.08]
                  px-4 py-2
                  text-sm font-semibold text-white/80
                  shadow-[0_18px_70px_rgba(0,0,0,0.25)]
                  transition
                "
              >
                <span className="opacity-70">←</span> Back
              </Link>
            )}

            <div className="hidden sm:flex items-center gap-2 text-xs text-white/45">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-300/70" />
              <span>Dashboard</span>
              <span className="opacity-30">/</span>
              <span className="text-white/70">Upload Artwork</span>
            </div>
          </div>

          <Link
            to="/arthouse/gallery"
            className="
              inline-flex items-center justify-center
              rounded-full border border-sky-300/25
              bg-sky-300/10 hover:bg-sky-300/15
              px-4 py-2
              text-sm font-semibold text-sky-200
              shadow-[0_18px_70px_rgba(0,0,0,0.25)]
              transition
            "
          >
            View Gallery
          </Link>
        </div>

        {/* Flash / Error */}
        {flash ? (
          <div className="mt-4 rounded-2xl border border-sky-300/25 bg-sky-300/10 px-4 py-3 text-sm text-sky-200 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
            {flash}
          </div>
        ) : null}

        {errorMsg ? (
          <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-white/80 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
            {errorMsg}
          </div>
        ) : null}

        {merchErrors.length ? (
          <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200">
            Fix merch links:{" "}
            <span className="text-white/70">
              {merchErrors.slice(0, 2).map((e, idx) => (
                <span key={idx}>
                  Link {e.idx + 1}: {e.msg}
                  {idx === 1 ? "" : " • "}
                </span>
              ))}
              {merchErrors.length > 2 ? ` +${merchErrors.length - 2} more` : ""}
            </span>
          </div>
        ) : null}

        {/* Main grid */}
        <div className="mt-7 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left panel */}
          <div className="lg:col-span-5">
            <div className="rounded-[30px] border border-white/10 bg-white/[0.055] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.6)] overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] tracking-[0.36em] uppercase text-white/55">
                      Arthouse
                    </div>

                    <h1 className="mt-2 text-[2.2rem] leading-[1.05] font-extrabold tracking-tight text-white/90">
                      Upload Artwork
                    </h1>

                    <p className="mt-3 text-sm text-white/65 leading-relaxed">
                      Post a new piece to the feed. Add clean shop links if you
                      sell this work.
                    </p>
                  </div>

                  <div
                    className={`
                      inline-flex items-center gap-2
                      rounded-full border px-3 py-2
                      text-xs font-semibold
                      ${
                        saving
                          ? "border-white/10 bg-white/5 text-white/60"
                          : "border-sky-300/25 bg-sky-300/10 text-sky-200"
                      }
                    `}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        saving ? "bg-white/30" : "bg-sky-300/80"
                      }`}
                    />
                    {saving ? "Publishing…" : "Ready"}
                  </div>
                </div>

                {/* image upload */}
                <div className="mt-6 rounded-[24px] border border-white/10 bg-black/15 overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-white/85">
                          Artwork Image
                        </div>
                      </div>

                    </div>

                    <div className="mt-4">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) =>
                          setSelectedFile(e.target.files?.[0] || null)
                        }
                        className="block w-full text-sm text-white/70
                          file:mr-4 file:rounded-xl file:border file:border-white/12
                          file:bg-white/[0.06] file:px-4 file:py-2
                          file:text-sm file:font-semibold file:text-white/80
                          hover:file:bg-white/[0.10] file:transition"
                      />

                      {selectedFile ? (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                          <div className="min-w-0">
                            <div className="text-xs text-white/45">
                              Selected file
                            </div>
                            <div className="truncate text-sm font-semibold text-white/80">
                              {selectedFile.name}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedFile(null)}
                            className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-white/70 flex items-center justify-center"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-white/45">
                          No file selected yet.
                        </div>
                      )}
                    </div>

                    <div className="mt-4 text-xs text-white/45">
                      Tip: PNG for art, JPG for photos. Keep under ~10MB.
                    </div>
                  </div>

                  <div className="h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />

                  {/* Posting checklist */}
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-white/85">
                          Posting checklist
                        </div>
                        <div className="mt-1 text-xs text-white/55">
                          Keep the feed premium + consistent.
                        </div>
                      </div>
                      <span className="text-[11px] tracking-[0.32em] uppercase text-white/45">
                        Clean
                      </span>
                    </div>

                    <ul className="mt-4 space-y-3 text-sm text-white/65">
                      <li className="flex gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-sky-300/70" />
                        <span>Short title. Strong thumbnail.</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-sky-300/55" />
                        <span>
                          Use consistent tags (anime, character, portrait,
                          concept).
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-sky-300/40" />
                        <span>Only add shop links if real.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 text-xs text-white/40">
                  Tip: one strong paragraph beats a wall of text.
                </div>
              </div>
            </div>
          </div>

          {/* Right panel (form shell) */}
          <div className="lg:col-span-7">
            <div className="rounded-[30px] border border-white/10 bg-white/[0.055] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.6)] overflow-hidden">
              <div className="px-6 pt-6 pb-4 border-b border-white/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[11px] tracking-[0.36em] uppercase text-white/55">
                      Details
                    </div>
                    <div className="mt-1 text-sm text-white/65">
                      Title, caption, tags, and optional shop links.
                    </div>
                  </div>

                  <div className="text-xs text-white/45">
                    {saving ? "Publishing…" : "Draft"}
                  </div>
                </div>
              </div>

              <div
                className="
                  p-6

                  [&_label]:text-sm
                  [&_label]:font-semibold
                  [&_label]:text-white/70

                  [&_input]:w-full
                  [&_textarea]:w-full
                  [&_select]:w-full

                  [&_input]:rounded-2xl
                  [&_textarea]:rounded-2xl
                  [&_select]:rounded-2xl

                  [&_input]:border
                  [&_textarea]:border
                  [&_select]:border
                  [&_input]:border-white/12
                  [&_textarea]:border-white/12
                  [&_select]:border-white/12

                  [&_input]:bg-black/25
                  [&_textarea]:bg-black/25
                  [&_select]:bg-black/25

                  [&_input]:text-slate-200
                  [&_textarea]:text-slate-200
                  [&_select]:text-slate-200

                  [&_input::placeholder]:text-white/35
                  [&_textarea::placeholder]:text-white/35

                  [&_input]:px-4
                  [&_textarea]:px-4
                  [&_select]:px-4
                  [&_input]:py-3
                  [&_select]:py-3
                  [&_textarea]:py-3
                  [&_textarea]:min-h-[140px]

                  [&_input:focus]:outline-none
                  [&_textarea:focus]:outline-none
                  [&_select:focus]:outline-none

                  [&_input:focus]:ring-2
                  [&_textarea:focus]:ring-2
                  [&_select:focus]:ring-2
                  [&_input:focus]:ring-sky-400/30
                  [&_textarea:focus]:ring-sky-400/30
                  [&_select:focus]:ring-sky-400/30

                  [&_input:focus]:border-sky-300/25
                  [&_textarea:focus]:border-sky-300/25
                  [&_select:focus]:border-sky-300/25

                  [&_input]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
                  [&_textarea]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
                  [&_select]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
                "
              >
                <div className="grid gap-4">
                  {/* Title */}
                  <div>
                    <label>Title</label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Short title"
                      maxLength={80}
                    />
                    <div className="mt-1 text-xs text-white/45">
                      {title.length}/80
                    </div>
                  </div>

                  {/* Caption */}
                  <div>
                    <label>Caption</label>
                    <textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Short, clean caption…"
                      maxLength={2200}
                    />
                    <div className="mt-1 text-xs text-white/45">
                      {caption.length}/2200
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label>Tags (comma separated)</label>
                    <input
                      value={tagsText}
                      onChange={(e) => setTagsText(e.target.value)}
                      placeholder="anime, portrait, character"
                    />

                    <div className="mt-2 flex flex-wrap gap-2">
                      {tagsArr.slice(0, 12).map((t) => (
                        <span
                          key={t}
                          className="text-xs rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-white/70"
                        >
                          #{t}
                        </span>
                      ))}
                      {tagsArr.length > 12 ? (
                        <span className="text-xs text-white/45">
                          +{tagsArr.length - 12} more
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Merch editor (same as EditArtworkPage) */}
                  <MerchLinksEditor
                    links={links}
                    setLinks={setLinks}
                    merchErrors={merchErrors}
                  />

                  {/* Actions */}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="
                        h-11 px-5 rounded-2xl
                        border border-white/10 bg-white/[0.04]
                        hover:bg-white/[0.07]
                        transition text-sm font-semibold text-white/80
                      "
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={onPublish}
                      disabled={!canPublish}
                      className="
                        h-11 px-6 rounded-2xl
                        border border-sky-300/25 bg-sky-300/10
                        hover:bg-sky-300/15
                        transition text-sm font-semibold text-sky-200
                        disabled:opacity-50
                      "
                    >
                      {saving ? "Publishing…" : "Publish"}
                    </button>
                  </div>

                  {!canPublish ? (
                    <div className="text-xs text-white/40">
                      Required: title, caption, image, and valid merch links (if
                      filled).
                    </div>
                  ) : (
                    <div className="text-xs text-white/40">
                      Your post will go live immediately after upload completes.
                    </div>
                  )}
                </div>

                <div className="mt-6 rounded-[24px] border border-white/10 bg-black/15 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm text-white/60">
                      <span className="font-semibold text-white/80">Pro tip:</span>{" "}
                      set 1 “Featured” shop link so buyers don’t hesitate.
                    </div>
                    <Link
                      to="/arthouse/gallery"
                      className="text-sm font-semibold text-sky-200/90 hover:text-sky-200 transition"
                    >
                      Preview the feed →
                    </Link>
                  </div>
                </div>

                <div className="mt-4 text-xs text-white/35">
                  Dark • Minimal • Sky accent • Thin borders • Soft text
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}



